// components/orders/FutureGiftModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseIcon } from '../../public/icons';
import MagnetsFrameModal from './MagnetsFrameModal';
import AudioGiftModal from './AudioGiftModal';
import orderService from '../../services/ordersRouteServce';

const FutureGiftModal = ({ isOpen, onClose, order, gift, onRefresh }) => {
  const navigate = useNavigate();

  /* 🟢 Inline modals for content creation */
  const [magnetsModal, setMagnetsModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null
  });
  const [audioModal, setAudioModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null
  });

  /* 🟢 Fetched content for this gift */
  const [content, setContent] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const orderId = order?.id || order?._id;
  const orderNumber = order?.orderNumber || order?.id;
  const giftId = gift?.id;

  /* ── Load this gift's content on open ── */
  useEffect(() => {
    if (!isOpen || !orderId || !giftId) return;

    let cancelled = false;
    setIsLoadingContent(true);

    (async () => {
      try {
        const res = await orderService.getFutureGiftContent(orderId, giftId);
        if (cancelled) return;
        setContent(res?.data || null);
      } catch (err) {
        console.error('Failed to load gift content:', err);
        if (!cancelled) setContent(null);
      } finally {
        if (!cancelled) setIsLoadingContent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId, giftId]);

  if (!isOpen || !order || !gift) return null;

  /* ── Derived values ── */
  const hasContent = !!content?.hasContent || !!gift.hasContent;
  const qrCodeUrl = content?.qrCodeUrl || gift.qrCodeUrl || '';
  const publicUrl = content?.publicUrl || gift.publicUrl || '';
  const revealDate = content?.revealDate || gift.deliveryDate || null;

  const revealDateObj = revealDate ? new Date(revealDate) : null;
  const daysUntil = revealDateObj
    ? Math.max(
        0,
        Math.ceil((revealDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      )
    : null;

  const formatLongDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return String(d);
    }
  };

  const giftTypeLabel = (type) => {
    switch (type) {
      case 'digital-memory':
        return 'Digital Memory';
      case 'magnets-frame':
        return 'Memory magnet';
      case 'audio-gift':
        return 'Audio Gift';
      default:
        return type || 'Future Gift';
    }
  };

  /* ── Address lines ── */
  const shippingLines = (() => {
    if (order.delivery) {
      const d = order.delivery;
      return [
        d.address,
        [d.city, d.state].filter(Boolean).join(', '),
        d.country,
        d.postalCode
      ].filter(Boolean);
    }
    if (order.shippingAddress) return [order.shippingAddress];
    return [];
  })();

  /* ── Timeline ── */
  const timelineSteps = [
    {
      id: 'confirmed',
      label: 'Order Confirmed',
      description: "We've received your order",
      date: order?.timeline?.confirmed || order?.date || '',
      completed: true
    },
    {
      id: 'content_prepared',
      label: 'Content Prepared',
      description: 'Your memory is being carefully put together.',
      date: order?.timeline?.production || '',
      completed: hasContent
    },
    {
      id: 'scheduled',
      label: 'Scheduled for reveal',
      description: 'Your gift has been scheduled for reveal.',
      date: '',
      completed: false
    },
    {
      id: 'revealed',
      label: 'Revealed',
      description: 'Your gift is going to be revealed today.',
      date: '',
      completed: false
    }
  ];

  /* ── Actions ── */
  const handleGiftAction = () => {
    if (!giftId) {
      console.warn(
        'Future gift has no id — run the backfill script before editing.'
      );
      return;
    }

    if (gift.giftType === 'magnets-frame') {
      setMagnetsModal({ open: true, orderId, giftId, gift });
      return;
    }

    if (gift.giftType === 'audio-gift') {
      setAudioModal({ open: true, orderId, giftId, gift });
      return;
    }

    const baseState = {
      gift,
      giftId,
      orderId,
      orderNumber,
      isEditing: hasContent
    };

    switch (gift.giftType) {
      case 'digital-memory':
        navigate('/account/future-gifts/create-memory', { state: baseState });
        break;
      default:
        navigate('/future-gifts/create', { state: baseState });
        break;
    }

    onClose();
  };

  const handleViewOrder = () => {
    onClose?.();
    navigate('/account/orders/all', {
      state: { openOrderId: orderId }
    });
  };

  const handleSaved = async () => {
    try {
      if (onRefresh) await onRefresh();

      /* Re-fetch this gift's content so the modal updates in place */
      if (orderId && giftId) {
        const res = await orderService.getFutureGiftContent(orderId, giftId);
        setContent(res?.data || null);
      }
    } catch (err) {
      console.error('Refresh after save failed:', err);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] text-left bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out shadow-2xl">
        <div className="h-full flex flex-col">
          {/* ── Header ── */}
          <div className="flex items-start justify-between p-6 pb-4 mb-4 border-b border-[#DFDFDF]">
            <div className="text-left min-w-0">
              <h2 className="text-2xl font-bold text-[#1C2A46] truncate">
                {gift.title || giftTypeLabel(gift.giftType)}
              </h2>
              <p className="text-[#6B635D] text-sm mt-0.5">
                {order.date} {order.time}
              </p>
            </div>

            <button
              onClick={onClose}
              className="text-[#1E1E1E] hover:text-opacity-70 border border-[#DFDFDF] p-2 flex-shrink-0"
              type="button"
            >
              <CloseIcon className="w-4 h-4 text-[#4A5568]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-8">
            {/* ═══════ Future Gifts header ═══════ */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#1C2A46] mb-3">
                Future Gifts
              </h3>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <p className="text-sm text-[#2D3748]">
                  <span className="text-[#718096]">
                    Delivery Scheduled:
                  </span>{' '}
                  {formatLongDate(revealDate)}
                </p>

                {daysUntil !== null && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#C08B2E]">
                      <svg
                        className="w-3 h-3 text-[#C08B2E]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path strokeLinecap="round" d="M12 7v5l3 2" />
                      </svg>
                    </span>
                    <span className="text-sm text-[#718096]">
                      {daysUntil} {daysUntil === 1 ? 'day' : 'days'} until
                      Delivery
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-[#2D3748]">
                  <span className="text-[#718096]">Linked Order:</span>{' '}
                  <button
                    onClick={handleViewOrder}
                    className="text-[#55000C] underline hover:text-[#6a0a17]"
                  >
                    Order #{orderNumber}
                  </button>
                </p>

                <button
                  type="button"
                  onClick={handleViewOrder}
                  className="inline-flex items-center gap-1 text-sm font-normal text-[#2D3748] hover:underline flex-shrink-0"
                >
                  View Order
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              <span className="inline-block mt-3 px-3 py-1 text-[#0088FF] bg-[#0088FF]/10 text-xs font-medium rounded">
                Scheduled
              </span>
            </div>

            {/* ═══════ Shipping Address ═══════ */}
            <div className="mb-6 border border-[#E4E4E4]  p-3">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-[#1C2A46] text-base">
                  Shipping Address
                </h4>
                {/* <button className="text-sm text-[#1C2A46] underline">
                  Edit Address
                </button> */}
              </div>
              <div className="text-[#2D3748] text-sm leading-relaxed">
                {shippingLines.length > 0 ? (
                  shippingLines.map((line, i) => <p key={i}>{line}</p>)
                ) : (
                  <p className="text-[#718096]">No address on file</p>
                )}
              </div>
            </div>

            {/* ═══════ Content Details ═══════ */}
            <div className="mb-6 border-2 border-dashed border-[#E4E4E4]  p-3">
              <h4 className="font-medium text-[#1C2A46] text-lg mb-3">
                Content Details
              </h4>

              <div className="flex items-start gap-3 mb-3">
                <span className="mt-0.5 flex-shrink-0">
                  {hasContent ? (
                    <svg
                      className="w-5 h-5 text-[#2F8F5B]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 12l3 3 5-6"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 text-[#C08B2E]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path strokeLinecap="round" d="M12 8v4M12 16h.01" />
                    </svg>
                  )}
                </span>

                <div className="min-w-0">
                  <p
                    className={`text-sm font-normal ${
                      hasContent ? 'text-[#175927]' : 'text-[#C08B2E]'
                    }`}
                  >
                    {hasContent ? 'Content Uploaded' : 'Content Required'}
                  </p>

                  <p className="text-sm text-[#6B635D] mt-0.5">
                    {hasContent
                      ? `Your photos and voice notes are safely stored. You can make changes until ${formatLongDate(
                          revealDate
                        )}.`
                      : 'Future gift content needs to be uploaded before the next delivery date.'}
                  </p>
                </div>
              </div>

              {/* Gift row — Edit or Add Content depending on state */}
              <div className="mt-4 flex items-center justify-between gap-4  ">
                <p className="text-sm font-normal text-[#1C2A46] truncate">
                  {giftTypeLabel(gift.giftType)}
                  {gift.giftType === 'magnets-frame' &&
                    Array.isArray(content?.magnets) &&
                    content.magnets.length > 0 &&
                    ` (${content.magnets.length} magnets)`}
                </p>

                <button
                  type="button"
                  onClick={handleGiftAction}
                  disabled={isLoadingContent}
                  className="text-sm font-medium text-[#55000C] hover:underline flex-shrink-0 disabled:opacity-60 disabled:cursor-wait"
                >
                  {isLoadingContent
                    ? 'Loading…'
                    : hasContent
                    ? 'Edit'
                    : 'Add Content'}
                </button>
              </div>
            </div>

            {/* ═══════ Order Timeline ═══════ */}
            <div className="mb-6">
              <div className="space-y-6">
                {timelineSteps.map((step, index) => (
                  <div key={step.id} className="relative">
                    {index < timelineSteps.length - 1 && (
                      <div className="absolute left-[15px] top-8 bottom-0 w-[2px] bg-[#E2E8F0] -mb-6" />
                    )}

                    <div className="flex items-start gap-4">
                      <div
                        className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          step.completed
                            ? 'border-[#55000C]'
                            : 'border-[#DBDBDB]'
                        }`}
                      >
                        {step.completed && (
                          <svg
                            className="w-4 h-4 text-[#55000C]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h4
                            className={`font-medium text-base ${
                              step.completed
                                ? 'text-[#1C2A46]'
                                : 'text-[#6B635D]'
                            }`}
                          >
                            {step.label}
                          </h4>
                          {step.date && (
                            <span className="text-sm font-light text-[#1C2A46] ml-4 flex-shrink-0">
                              {step.date}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-light text-[#6B635D] mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ═══════ QR Code Status ═══════ */}
            {qrCodeUrl && (
              <div className="mb-6 border border-[#E4E4E4]  p-3">
                <h4 className="font-semibold text-[#1C2A46] text-base mb-4">
                  QR Code Status
                </h4>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="w-32 h-32 object-contain flex-shrink-0 "
                  />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#175927] mb-1">
                      {revealDateObj
                        ? `Locked until ${formatLongDate(revealDateObj)}`
                        : 'QR code ready'}
                    </p>
                    <p className="text-sm font-light text-[#6B635D]">
                      Anyone who scans the QR code before this date will see a
                      countdown.
                    </p>

                    
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inline modals for content creation */}
      <MagnetsFrameModal
        isOpen={magnetsModal.open}
        onClose={() =>
          setMagnetsModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null
          })
        }
        orderId={magnetsModal.orderId}
        giftId={magnetsModal.giftId}
        gift={magnetsModal.gift}
        orderNumber={orderNumber}
        onSaved={handleSaved}
      />

      <AudioGiftModal
        isOpen={audioModal.open}
        onClose={() =>
          setAudioModal({
            open: false,
            orderId: null,
            giftId: null,
            gift: null
          })
        }
        orderId={audioModal.orderId}
        giftId={audioModal.giftId}
        gift={audioModal.gift}
        orderNumber={orderNumber}
        onSaved={handleSaved}
      />
    </>
  );
};

export default FutureGiftModal;